import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData, ImageFileUpload } from "../../../utility/Utility";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

export default function AddTeamMember() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    teamMemberName: "",
    designation: "",
    description: "",
    memberType: "KP Startup Hub Board Members",
    image: null,
    order: 1,
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image") {
      setFormData({ ...formData, image: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Function to upload image
  const uploadImage = async () => {
    if (!formData.image) return null;
    const imageFormData = new FormData();
    imageFormData.append("file", formData.image);

    try {
      const response = await ImageFileUpload("/api/admin/upload?is_upload=s3", imageFormData);
      if (response?.success) {
        return response.url || response.imageUrl || response.filePath;
      } else {
        toast.error(response.message || "Image upload failed");
        return null;
      }
    } catch (error) {
      toast.error("Network error during image upload");
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const uploadedImagePath = await uploadImage();
      if (!uploadedImagePath && formData.image) {
        setLoading(false);
        return;
      }

      const payload = {
        teamMemberName: formData.teamMemberName,
        designation: formData.designation,
        description: formData.description,
        memberType: formData.memberType,
        image: uploadedImagePath,
        order: parseInt(formData.order),
      };

      const response = await postData("api/team-members/create", payload);

      if (response.success) {
        toast.success("Team member added successfully!");
        navigate("/team-member-list");
      } else {
        toast.error(response.message || "Failed to add team member");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
      console.error("Add team member error:", error);
    } finally {
      setLoading(false);
    }
  };

  const memberTypeOptions = [
    { value: "KP Startup Hub Board Members", label: "KP Startup Hub Board Members" },
    { value: "Operational Team", label: "Operational Team" },
    { value: "IT Team", label: "IT Team" },
  ];

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={5000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <div className="page-title-box">
                <h4 className="mb-1">Team Member Management</h4>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">Team Member</li>
                  <li className="breadcrumb-item active">Add Team Member</li>
                </ol>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate("/team-member-list")}
              disabled={loading}
            >
              <i className="fas fa-arrow-left me-1"></i> Back
            </button>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Add New Team Member</h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    {/* Team Member Name */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="teamMemberName" className="form-label fw-medium">
                          Team Member Name <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          id="teamMemberName"
                          name="teamMemberName"
                          className="form-control"
                          placeholder="Enter team member name"
                          value={formData.teamMemberName}
                          onChange={handleInputChange}
                          required
                          minLength={2}
                          maxLength={100}
                        />
                      </div>
                    </div>

                    {/* Designation */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="designation" className="form-label fw-medium">
                          Designation <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="text"
                          id="designation"
                          name="designation"
                          className="form-control"
                          placeholder="Enter designation"
                          value={formData.designation}
                          onChange={handleInputChange}
                          required
                          minLength={2}
                          maxLength={100}
                        />
                      </div>
                    </div>

                    {/* Member Type */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="memberType" className="form-label fw-medium">
                          Member Type <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <select
                          id="memberType"
                          name="memberType"
                          className="form-control"
                          value={formData.memberType}
                          onChange={handleInputChange}
                          required
                        >
                          {memberTypeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <label htmlFor="description" className="form-label fw-medium">
                          Description <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <textarea
                          className="form-control"
                          id="description"
                          name="description"
                          rows={4}
                          placeholder="Enter description..."
                          value={formData.description}
                          onChange={handleInputChange}
                          required
                          minLength={10}
                        />
                        <small className="text-muted">Minimum 10 characters</small>
                      </div>
                    </div>

                    {/* Image Upload */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="image" className="form-label fw-medium">
                          Member Image <span className="text-danger">*</span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="file"
                          id="image"
                          name="image"
                          className="form-control"
                          onChange={handleInputChange}
                          accept="image/*"
                          required
                        />
                        <small className="text-muted d-block mt-1">
                          Recommended: 400x400px, Max 2MB (JPEG/PNG)
                        </small>
                        {formData.image && (
                          <div className="mt-2">
                            <img
                              src={URL.createObjectURL(formData.image)}
                              alt="Preview"
                              className="img-thumbnail"
                              style={{ maxWidth: "200px", maxHeight: "150px" }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Order */}
                    <div className="row mb-3 align-items-center">
                      <div className="col-md-3">
                        <label htmlFor="order" className="form-label fw-medium">
                          Display Order
                        </label>
                      </div>
                      <div className="col-md-6">
                        <input
                          type="number"
                          id="order"
                          name="order"
                          className="form-control"
                          placeholder="Enter display order"
                          value={formData.order}
                          onChange={handleInputChange}
                          min="1"
                        />
                        <small className="text-muted">Lower numbers appear first</small>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="row mt-4">
                      <div className="col-md-6 offset-md-3">
                        <button
                          type="submit"
                          className="btn btn-primary py-2"
                          disabled={loading}
                        >
                          {loading ? "Processing..." : "Submit"}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
